/** Tiny DOM helpers. No framework: the whole app is a handful of screens. */

type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, attrs: Attrs = {}, children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v as EventListener);
    else if (k === 'text') node.textContent = String(v);
    else if (k === 'html') node.innerHTML = String(v);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) node.append(c);
  return node;
}

export function mount(...nodes: Node[]): void {
  const app = document.getElementById('app')!;
  app.replaceChildren(...nodes);
}

/**
 * Human time remaining. Always rendered as text beside the ring: a countdown that
 * relies on colour alone fails anyone who cannot distinguish the ramp.
 */
export function remaining(expiresAt: number | null, nowMs: number): string {
  if (expiresAt === null) return 'Kept';
  const ms = expiresAt - nowMs;
  if (ms <= 0) return 'Gone';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins} min left`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} left`;
  const days = Math.round(ms / 86_400_000);
  return `${days} ${days === 1 ? 'day' : 'days'} left`;
}

/** Fraction of life remaining, for the ring and its colour band. */
export function lifeLeft(createdAt: number, expiresAt: number | null, nowMs: number): number {
  if (expiresAt === null) return 1;
  const total = expiresAt - createdAt;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, (expiresAt - nowMs) / total));
}

export function band(fraction: number): 'accent' | 'warn' | 'urgent' {
  if (fraction <= 0.1) return 'urgent';
  if (fraction <= 0.25) return 'warn';
  return 'accent';
}

/** The countdown ring. Kept items get no ring at all — a shape difference, not
 *  just a colour one, so "kept" can never be mistaken for "running out". */
export function ring(fraction: number, expiresAt: number | null, label: string): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'ring');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', label);
  if (expiresAt === null) {
    const dot = document.createElementNS(ns, 'circle');
    dot.setAttribute('cx', '12'); dot.setAttribute('cy', '12'); dot.setAttribute('r', '4');
    dot.setAttribute('fill', 'var(--keep)');
    svg.append(dot);
    return svg;
  }
  const r = 9;
  const c = 2 * Math.PI * r;
  for (const [cls, dash] of [['track', String(c)], ['fill', `${c * fraction} ${c}`]] as const) {
    const circle = document.createElementNS(ns, 'circle');
    circle.setAttribute('cx', '12'); circle.setAttribute('cy', '12'); circle.setAttribute('r', String(r));
    circle.setAttribute('fill', 'none');
    circle.setAttribute('stroke-width', '2.5');
    circle.setAttribute('stroke-linecap', 'round');
    circle.setAttribute('stroke-dasharray', dash);
    circle.setAttribute('transform', 'rotate(-90 12 12)');
    circle.setAttribute('class', cls);
    svg.append(circle);
  }
  svg.setAttribute('data-band', band(fraction));
  return svg;
}

export function bitmapUrl(bytes: Uint8Array, type = 'image/jpeg'): string {
  return URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type }));
}
